import type { ReactNode } from 'react';

export type RingTone = 'green' | 'blue' | 'purple' | 'orange' | 'red' | 'yellow' | 'teal' | 'grey';

/** Tone from a score: green from `good`, yellow from `mid`, red below. */
export const toneFor = (value: number, good = 70, mid = 40): RingTone => (value >= good ? 'green' : value >= mid ? 'yellow' : 'red');

/**
 * A progress ring with the % (or any text) in the middle, the same design as Home's "My goal".
 * `extra` draws a lighter second arc after the first (e.g. open pipeline after won).
 */
export function Ring({ value, extra = 0, tone = 'green', size = 'md', center, caption, title }: {
  value: number; extra?: number; tone?: RingTone; size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Text in the middle; defaults to the rounded %. */
  center?: ReactNode; caption?: ReactNode; title?: string;
}) {
  const v = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
  const e = Math.max(0, Math.min(100 - v, extra));
  const R = 54, C = 2 * Math.PI * R;
  const arc = (from: number, len: number) => ({ strokeDasharray: `${(len / 100) * C} ${C}`, strokeDashoffset: -(from / 100) * C });
  const pct = v > 0 && v < 10 && v % 1 !== 0 ? v.toFixed(1) : Math.round(v).toString();
  return (
    <span className={`ring ring-${size} tone-${tone}`} role="img" aria-label={title ?? `${pct}%`} title={title}>
      <svg viewBox="0 0 140 140" aria-hidden="true">
        <circle cx="70" cy="70" r={R} className="ring-track" />
        {e > 0 && <circle cx="70" cy="70" r={R} className="ring-extra" style={arc(v, e)} />}
        {/* Round ends only once the arc is long enough, so a tiny value doesn't become a blob. */}
        {v > 0 && <circle cx="70" cy="70" r={R} className={`ring-val${v < 4 ? ' flat' : ''}${v >= 100 ? ' done' : ''}`} style={arc(0, v)} />}
      </svg>
      <span className="ring-text">
        <b>{center ?? `${pct}%`}</b>
        {caption && size !== 'xs' && <span>{caption}</span>}
      </span>
    </span>
  );
}
